/**
 * What a vendor-created booking actually collects online.
 *
 * Mirrors the server's `calculatePartialPayment` (bookyourzone_be
 * `src/services/calculatePartialPayment.js`) for the VENDOR booking source, so
 * the modal can state the split before the booking exists. Two things there are
 * easy to get wrong from the venue's settings alone:
 *
 *   - a vendor-created booking is charged `onlineDBLPaymentPercentage`, NOT
 *     `onlinePaymentPercentage` — the latter is the rate for customer-app
 *     bookings. Showing that one would overstate the link by the difference
 *     between the two rates.
 *   - `partialPaymentType`, `fixedOnlineAmount` and `minOnlineAmount` exist on
 *     the schema but nothing reads them today, so they are deliberately not
 *     applied here. Honour them only when the server starts to.
 *
 * The advance is taken on the vendor's base price only. BookYourZone's flat
 * platform fee and its GST ride on top of the customer's link and are not known
 * to the dashboard, which is why the UI names them rather than totalling them.
 */

export interface VenuePaymentSettings {
  allowPartialPayment?: boolean;
  onlinePaymentPercentage?: number;
  onlineDBLPaymentPercentage?: number;
  /** Declared on the venue schema, unused by the split today. */
  partialPaymentType?: 'PERCENTAGE' | 'FIXED';
  fixedOnlineAmount?: number | null;
  minOnlineAmount?: number | null;
}

/** The server's fallback when a venue carries no DBL rate. */
export const VENDOR_ADVANCE_FALLBACK_PERCENT = 10;

/**
 * The share of the online advance the vendor keeps if the customer cancels.
 *
 * A stated business policy, not something the server computes yet: refunds run
 * off the `CancellationPolicy` bands in `bookingPaymentController`, which keep
 * 10% with notice and everything inside six hours, and that handler issues the
 * customer's refund without settling a retained share back to the vendor. The
 * figure here is what vendors are told; revisit both together when the payout
 * side is built.
 *
 * Applies to the online amount only — cash the venue never collected is not
 * refundable in the first place.
 */
export const VENDOR_CANCELLATION_SHARE_PERCENT = 80;

/** What the vendor keeps of a cancelled booking's online advance. */
export const cancellationShare = (onlineNow: number) =>
  Math.round((onlineNow * VENDOR_CANCELLATION_SHARE_PERCENT) / 100);

export interface BookingPaymentSplit {
  /** False when the venue takes the whole amount online. */
  partial: boolean;
  /** Share of the venue's own price collected by the payment link. */
  percent: number;
  /** Collected by the Razorpay link, before platform fee and GST. */
  onlineNow: number;
  /** Collected by the venue itself, at the counter. */
  atVenue: number;
}

export const splitBookingPayment = (
  vendorBaseTotal: number,
  settings?: VenuePaymentSettings | null,
): BookingPaymentSplit => {
  const total = Number.isFinite(vendorBaseTotal) ? Math.max(vendorBaseTotal, 0) : 0;

  if (settings?.allowPartialPayment !== true) {
    return { partial: false, percent: 100, onlineNow: total, atVenue: 0 };
  }

  const percent =
    settings.onlineDBLPaymentPercentage || VENDOR_ADVANCE_FALLBACK_PERCENT;

  const onlineNow = Math.round((total * percent) / 100);

  // Subtracted rather than computed, so the two halves always add back to the
  // total however the rounding fell.
  return { partial: true, percent, onlineNow, atVenue: total - onlineNow };
};
